/**
 * Zentrale Tages-Finanzsummen — einheitlich für Tagesabschluss, Manager-Dashboard, Berichte.
 * Verkäufe: getSaleTotalAmount | Liquidität: computeLiquidBalanceByMethod | Accrual: entryTotalAmount
 */

import { summarizeRevenue } from './revenue';
import { computeLiquidBalanceByMethod, resolveOutflowMethodKey } from './cashflow';
import { entryTotalAmount, isSetupPhase } from '../../utils/fundingSource';

function sumEntries(entries, getAmount = entryTotalAmount) {
  return (entries || []).reduce((sum, entry) => sum + getAmount(entry), 0);
}

/** Paiements fournisseurs groupés par méthode (nombre + montant total). */
export function aggregateSupplierPaymentsByMethod(entries = []) {
  const bucket = new Map();

  for (const entry of entries) {
    if (!entry) continue;
    const methodKey = resolveOutflowMethodKey(entry);
    const row = bucket.get(methodKey) || { methodKey, count: 0, total: 0 };
    row.count += 1;
    row.total += entryTotalAmount(entry);
    bucket.set(methodKey, row);
  }

  return [...bucket.values()].sort((a, b) => b.total - a.total);
}

/**
 * @param {object} params — bereits auf den Abschlusstag gefilterte Arrays
 * @param {boolean} [params.installationPhase]
 * @param {object[]} [params.allSales] — full sales list to detect installation when `sales` is day-filtered
 * @param {object[]|null} [params.customerDebtPayments] — **day-scoped** Créance-Einzüge (preferred)
 * @param {object[]|null} [params.customerDebts] — full debt records; only for unscoped full-business sums
 */
export function computeDayFinancialSummary({
  sales = [],
  purchases = [],
  expenses = [],
  supermarketPayments = [],
  invoicePayments = [],
  supermarketInvoices = [],
  customerDebts = null,
  customerDebtPayments = null,
  installationPhase = null,
  allSales = null
} = {}) {
  const revenueSummary = summarizeRevenue(sales);
  const revenue = revenueSummary.totalRevenue;

  const purchasesTotal = sumEntries(purchases);
  const expensesTotal = sumEntries(expenses);

  const supplierPaymentEntries = [
    ...(supermarketPayments || []).filter((p) => p?.transactionType !== 'expense_payment'),
    ...(invoicePayments || [])
  ];
  const paymentsTotal = sumEntries(supplierPaymentEntries);
  const paymentsCount = supplierPaymentEntries.length;
  const paymentsByMethod = aggregateSupplierPaymentsByMethod(supplierPaymentEntries);

  const resolvedInstallation =
    installationPhase != null
      ? Boolean(installationPhase)
      : isSetupPhase({ sales: allSales != null ? allSales : sales });

  const liquid = computeLiquidBalanceByMethod({
    sales,
    expenses,
    supermarketPayments,
    invoicePayments,
    supermarketInvoices,
    customerDebts,
    customerDebtPayments,
    installationPhase: resolvedInstallation
  });

  const paymentTotals = {
    incoming: {
      total: liquid.total.income,
      cash: liquid.cash.income,
      electronic: liquid.electronic.income,
      count: revenueSummary.salesCount
    },
    outgoing: {
      total: liquid.total.outgoing,
      cash: liquid.cash.outgoing,
      electronic: liquid.electronic.outgoing,
      count:
        (expenses || []).length +
        (supermarketPayments || []).filter((p) => p?.transactionType !== 'expense_payment').length
    },
    balance: {
      total: liquid.total.balance,
      cash: liquid.cash.balance,
      electronic: liquid.electronic.balance
    },
    externalFinancing: liquid.total.externalFinancing || 0,
    financingExposure: liquid.total.financingExposure || 0,
    methods: liquid.methods,
    totalAmount: liquid.total.income
  };

  const outgoingAccrual = purchasesTotal + expensesTotal;
  // Register liquid only (not accrual revenue). Legacy name netCash kept for daily-closing compat.
  const netLiquid = liquid.total.balance;
  const netCash = netLiquid;

  return {
    revenue,
    purchasesTotal,
    expensesTotal,
    paymentsTotal,
    paymentsCount,
    paymentsByMethod,
    outgoing: outgoingAccrual,
    net: revenue - outgoingAccrual,
    /** @deprecated alias of netLiquid (was revenue − payments − expenses; misnamed). */
    netCash,
    netLiquid,
    outgoingRegister: liquid.total.outgoing,
    paymentTotals,
    liquid
  };
}
